import React, { useEffect, useState, useCallback } from 'react';
import { Spin } from 'antd';
import { useNavigate } from 'react-router-dom';
import HotProposalIcon from '../components/icons/HotProposalIcon';
import TeamFindIcon from '../components/icons/TeamFindIcon';
import IdeaIcon from '../components/icons/IdeaIcon';
import friendService from '../services/friendService';
import ExploreBanner from '../components/explore/ExploreBanner';
import ExploreSection from '../components/explore/ExploreSection';
import ExploreCard from '../components/explore/ExploreCard';

// Helper to map API content to card props
const mapContentToCard = (content) => {
  const fields = content.fields || {};
  
  // Handle author info
  let authorName = 'Unknown';
  let authorAvatar = '';
  
  if (content.user) {
    authorName = content.user.name || content.user.nickname || 'Unknown';
    authorAvatar = content.user.avatar || '';
  } else if (content.creator) {
    // Fallback if user object is not directly available but creator is
    authorName = content.creator.name || content.creator.nickname || 'Unknown';
    authorAvatar = content.creator.avatar || '';
  }
  
  // Process cover image URL
  let coverImage = null;
  if (fields.cover_image) {
    coverImage = fields.cover_image.startsWith('http') 
      ? fields.cover_image 
      : `/api/v2/contents/${fields.cover_image}/file`;
  }

  return {
    id: content.id,
    title: fields.title || fields.prompt || '无标题',
    description: fields.body_text || fields.response || fields.description || '',
    coverImage: coverImage,
    author: {
      name: authorName,
      avatar: authorAvatar
    },
    stats: {
      likes: content.interactions?.like_count || 0,
      is_liked: content.is_liked
    },
    tags: fields.tags || []
  };
};

const ExplorePage = () => {
  const [loading, setLoading] = useState(true);
  const [activities, setActivities] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [friends, setFriends] = useState([]);
  const [ideas, setIdeas] = useState([]);
  const [officialPosts, setOfficialPosts] = useState([]);
  
  const navigate = useNavigate();

  const handleAuthNavigation = useCallback((path) => {
    // 允许未登录用户访问详情页
    navigate(path);
  }, [navigate]);

  const fetchExploreData = useCallback(async () => {
    setLoading(true);
    try {
      // Parallel fetching of all sections
      const [competitionsRes, friendsRes, ideasRes, officialRes] = await Promise.all([
        // Competitions (Used for both Banner and Hot Proposals)
        // Fetch 5 items: Top 3 for Banner, Next 2 for Hot Proposals list
        friendService.searchContents({ 
          type: 'article', 
          category: 'competition',
          use_cache: true,
          filter: {
            exprs: [
               { field: 'status', op: 'EQ', values: ['published'] }
            ]
          },
          sort: { field: 'created_at', desc: true },
          pagination: { page: 1, per_page: 5 } 
        }).catch(err => ({ contents: [] })),
        
        // Find Teammates - Tags include '找队友'
        friendService.searchContents({ 
          type: 'article', 
          category: 'post',
          use_cache: true,
          filter: {
            exprs: [
               { field: 'tags', op: 'EQ', values: ['找队友'] },
               { field: 'status', op: 'EQ', values: ['published'] }
            ]
          },
          sort: { field: 'created_at', desc: true },
          pagination: { page: 1, per_page: 4 } 
        }).catch(err => ({ contents: [] })),
        
        // Find Ideas - Tags do NOT include '找队友'
        friendService.searchContents({ 
          type: 'article', 
          category: 'post',
          use_cache: true,
          filter: {
            exprs: [
               { field: 'tags', op: 'NOT_IN', values: ['找队友'] },
               { field: 'status', op: 'EQ', values: ['published'] }
            ]
          },
          sort: { field: 'created_at', desc: true },
          pagination: { page: 1, per_page: 4 } 
        }).catch(err => ({ contents: [] })),

        // Official Posts - Tags include 'official'
        friendService.searchContents({ 
          type: 'article', 
          category: 'post',
          use_cache: true,
          filter: {
            exprs: [
               { field: 'tags', op: 'EQ', values: ['official'] },
               { field: 'status', op: 'EQ', values: ['published'] }
            ]
          },
          sort: { field: 'created_at', desc: true },
          pagination: { page: 1, per_page: 2 } 
        }).catch(err => ({ contents: [] })),
      ]);

      // Process Competitions for Banner and List
      const allCompetitions = competitionsRes.contents || [];
      const bannerCompetitions = allCompetitions.slice(0, 3);
      const listCompetitions = allCompetitions.slice(3, 5);

      // Map for Banner
      const bannerItems = bannerCompetitions.map(item => ({
        id: item.id,
        title: item.fields?.title || '无标题',
        description: item.fields?.description || item.fields?.body_text || '',
        image: item.fields?.cover_image 
          ? (item.fields.cover_image.startsWith('http') ? item.fields.cover_image : `/api/v2/contents/${item.fields.cover_image}/file`) 
          : 'https://images.unsplash.com/photo-1550745165-9bc0b252726f?q=80&w=2670&auto=format&fit=crop',
      }));
      setActivities(bannerItems);

      // Map for Hot Proposals List
      setProposals(listCompetitions.map(mapContentToCard));

      setFriends((friendsRes.contents || []).map(mapContentToCard));
      setIdeas((ideasRes.contents || []).map(mapContentToCard));

      // Process Official Posts
      setOfficialPosts((officialRes?.contents || []).map(mapContentToCard).map((card, index) => ({
        ...card,
        tag: '官方',
        likes: card.stats.likes,
        is_liked: card.stats.is_liked,
        bgColor: index % 2 === 0 ? 'bg-blue-400' : 'bg-pink-400',
        image: card.coverImage || 'https://images.unsplash.com/photo-1517048676732-d65bc937f952?q=80&w=2670&auto=format&fit=crop'
      })));

    } catch (error) {
      console.error("Failed to fetch explore data", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchExploreData();
  }, [fetchExploreData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-w-0 py-24 text-gray-600">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-8 min-w-0">
      <div className="w-full max-w-[856px] mx-auto min-w-0">
        <ExploreBanner items={activities} sideItems={officialPosts} loading={loading} />
      </div>

      <ExploreSection 
        title="热门提案" 
        icon={<HotProposalIcon />}
        link="/explore/proposals"
      >
        <div className="flex flex-nowrap gap-6 overflow-x-auto">
          {proposals.map(item => (
            <ExploreCard 
              key={item.id} 
              {...item} 
              layout="horizontal"
              onClick={() => navigate(`/explore/proposals/${item.id}`)}
            />
          ))}
          {proposals.length === 0 && <div className="w-full text-gray-400 text-center py-8">暂无热门提案</div>}
        </div>
      </ExploreSection>

      <ExploreSection 
        title="找队友" 
        icon={<TeamFindIcon />}
        link="/explore/friends"
      >
        <div className="flex flex-nowrap gap-6 overflow-x-auto">
          {friends.map(item => (
            <ExploreCard 
              key={item.id} 
              {...item} 
              imageHeight="h-full"
              className="shrink-0"
              onClick={() => handleAuthNavigation(`/explore/friends/${item.id}`)}
            />
          ))}
          {friends.length === 0 && <div className="w-full text-gray-400 text-center py-8">暂无找队友信息</div>}
        </div>
      </ExploreSection>

      <ExploreSection 
        title="找点子" 
        icon={<IdeaIcon />}
        link="/explore/ideas"
      >
        <div className="flex flex-nowrap gap-6 overflow-x-auto">
          {ideas.map(item => (
            <ExploreCard 
              key={item.id} 
              {...item} 
              imageHeight="h-full"
              className="shrink-0"
              onClick={() => navigate(`/explore/friends/${item.id}`)}
            />
          ))}
          {ideas.length === 0 && <div className="w-full text-gray-400 text-center py-8">暂无点子信息</div>}
        </div>
      </ExploreSection>
    </div>
  );
};

export default ExplorePage;
